import { useEffect, useRef, useState, type ReactNode } from 'react';
import { BRAND, sofVisible } from '@platform/shared';
import { cx, Icon } from '../../components/ui';
import { Link, navigate, usePath } from '../../lib/router';
import { useCompliance } from '../../stores/account';
import { usePendingDocuments } from '../../stores/documents';
import { loadMe, useMe } from '../../stores/session';
import { accountLock } from '../onboarding/AccountLock';
import { AlertsPage } from './Alerts';
import { Dashboard } from './Dashboard';
import { DepositPage } from './Deposit';
import { DocumentPage, DocumentsPage } from './Documents';
import { NotificationsPage } from './Notifications';
import { DetailsTab, KycTab, PersonalTabs, SecurityTab, SourceOfFundsTab } from './Personal';
import { SupportPage } from './Support';
import { WithdrawPage } from './Withdraw';
import { WalletPage } from '../WalletPage';
import { AIcon, CopyButton, KycBadge, WarnIcon } from './kit';

const NAV = [
  { to: '/account', label: 'Dashboard', match: (p: string) => p === '/account' },
  { to: '/wallet', label: 'Wallet', match: (p: string) => p === '/wallet' },
  { to: '/account/personal', label: 'Personal Information', match: (p: string) => ['/account/personal', '/account/security', '/account/kyc', '/account/source-of-funds'].includes(p) },
  { to: '/account/deposit', label: 'Deposit', match: (p: string) => p === '/account/deposit' },
  { to: '/account/withdraw', label: 'Withdraw', match: (p: string) => p === '/account/withdraw' },
  { to: '/account/documents', label: 'Documents', match: (p: string) => p === '/account/documents' || p.startsWith('/account/documents/') },
  { to: '/account/alerts', label: 'Alerts', match: (p: string) => p === '/account/alerts' },
  { to: '/account/notifications', label: 'Notifications', match: (p: string) => p === '/account/notifications' || p === '/account/notifications/settings' },
  { to: '/account/support', label: 'Support', match: (p: string) => p === '/account/support' },
];

const TITLES: Record<string, string> = {
  '/account': 'Dashboard',
  '/wallet': 'Wallet',
  '/account/personal': 'Personal information',
  '/account/security': 'Security',
  '/account/kyc': 'Verification',
  '/account/source-of-funds': 'Source of funds',
  '/account/deposit': 'Deposit',
  '/account/withdraw': 'Withdraw',
  '/account/documents': 'Documents',
  '/account/alerts': 'Alerts',
  '/account/notifications': 'Notifications',
  '/account/notifications/settings': 'Notification settings',
  '/account/support': 'Support',
};

/** Documents waiting for the client's signature, beside the Documents entry. */
function PendingBadge({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span aria-label={`${n} to sign`} className="num grid h-5 min-w-5 place-items-center rounded-full bg-warn px-1.5 text-[11px] font-semibold text-white">
      {n > 99 ? '99+' : n}
    </span>
  );
}

/** Phones: one full-width button naming the current section; it opens the section list. */
function SectionPicker({ path, locked }: { path: string; locked: boolean }) {
  const [open, setOpen] = useState(false);
  const pending = usePendingDocuments();
  const box = useRef<HTMLElement>(null);
  const current = NAV.find((n) => n.match(path));
  useEffect(() => setOpen(false), [path]);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  return (
    <nav ref={box} aria-label="Account" className="relative w-full md:hidden">
      <button
        type="button"
        aria-expanded={open}
        aria-controls="account-sections"
        onClick={() => setOpen((o) => !o)}
        className="flex h-12 w-full items-center justify-between gap-3 rounded-full bg-track pr-4 pl-5 text-[15px] font-semibold text-fg"
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{current?.label ?? 'My account'}</span>
          {current?.to !== '/account/documents' && <PendingBadge n={pending} />}
        </span>
        <Icon name="chevron" size={18} strokeWidth={2.4} className={cx('shrink-0 text-muted transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <ul id="account-sections" className="absolute inset-x-0 top-[calc(100%+8px)] z-20 m-0 list-none rounded-2xl border border-line bg-card p-1.5 shadow-lg">
          {NAV.map((n) => {
            const on = n.match(path);
            return (
              <li key={n.to}>
                <Link
                  to={n.to}
                  onClick={() => setOpen(false)}
                  aria-current={on ? 'page' : undefined}
                  className={cx('flex h-12 items-center gap-2 rounded-xl px-4 text-[15px] font-semibold no-underline', on ? 'bg-track text-fg' : 'text-muted')}
                >
                  {n.label}
                  {n.to === '/account/withdraw' && locked && <AIcon name="lock" size={13} />}
                  {n.to === '/account/documents' && <PendingBadge n={pending} />}
                  {on && <Icon name="check" size={18} strokeWidth={2.4} className="ml-auto" />}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </nav>
  );
}

/** "ACC-7F3A91C2": short, readable form of the live account id. */
export const shortAccountId = (id: string) => `ACC-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}`;

export function AccountLayout() {
  const path = usePath().replace(/\/$/, '') || '/account';
  const me = useMe()!;
  const live = me.accounts.find((a) => a.kind === 'live');
  const scroller = useRef<HTMLDivElement>(null);
  // Refresh KYC status, names and balances whenever the account area opens.
  useEffect(() => {
    void loadMe().catch(() => {});
  }, []);

  useEffect(() => {
    document.title = `${TITLES[path] ?? (path.startsWith('/account/documents/') ? 'Document' : 'My account')} · ${BRAND.name}`;
    scroller.current?.scrollTo(0, 0);
  }, [path]);

  // A staff session that may not see the email gets it masked: no use as a name.
  const name = [me.firstName, me.lastName].filter(Boolean).join(' ') || (me.contactHidden ? 'Client' : me.email);
  const initials = (me.firstName && me.lastName ? me.firstName[0]! + me.lastName[0]! : me.email.slice(0, 2)).toUpperCase();
  const kyc = me.kycStatus;
  const compliance = useCompliance();
  const pending = usePendingDocuments();
  // A self sign-up whose account isn't open sees the status bar across the app instead.
  const showBanner = kyc !== 'approved' && path !== '/account' && path !== '/account/kyc' && !accountLock(me);
  // Once KYC is approved, what still holds withdrawals back: source of funds, then proof of address.
  // Shown only while the rules are enforced (otherwise nothing is actually held back).
  const sofBanner = kyc === 'approved' && !!compliance?.enforced && compliance.sofRequired && compliance.sofStatus !== 'pending' && path !== '/account/source-of-funds';
  const poaBanner =
    kyc === 'approved' && !sofBanner && !!compliance?.enforced && (compliance.poaStatus === 'update_required' || compliance.poaStatus === 'rejected') && path !== '/account/kyc';
  const due = compliance?.sofDueAt ? new Date(compliance.sofDueAt).toLocaleDateString('en', { day: 'numeric', month: 'short' }) : null;

  // Source of funds is hidden until the client's deposits reach the threshold; a direct link goes to KYC.
  const sofHidden = path === '/account/source-of-funds' && !!compliance && !sofVisible(compliance);
  useEffect(() => {
    if (sofHidden) navigate('/account/kyc', { replace: true });
  }, [sofHidden]);

  // The Reports page is gone (performance reports stay with the account manager's CRM): old links and
  // notifications land on the dashboard.
  useEffect(() => {
    if (path === '/account/reports') navigate('/account', { replace: true });
  }, [path]);

  let page: ReactNode;
  if (path === '/account' || path === '/account/reports') page = <Dashboard />;
  else if (path === '/wallet') page = <WalletPage />;
  else if (path === '/account/personal') page = <PersonalTabs tab="details"><DetailsTab /></PersonalTabs>;
  else if (path === '/account/security') page = <PersonalTabs tab="security"><SecurityTab /></PersonalTabs>;
  else if (path === '/account/kyc') page = <PersonalTabs tab="kyc"><KycTab /></PersonalTabs>;
  else if (path === '/account/source-of-funds') page = <PersonalTabs tab="sof"><SourceOfFundsTab /></PersonalTabs>;
  else if (path === '/account/deposit') page = <DepositPage />;
  else if (path === '/account/withdraw') page = <WithdrawPage />;
  else if (path === '/account/documents') page = <DocumentsPage />;
  else if (path.startsWith('/account/documents/')) page = <DocumentPage key={path} id={path.slice('/account/documents/'.length)} />;
  else if (path === '/account/alerts') page = <AlertsPage />;
  else if (path === '/account/notifications') page = <NotificationsPage tab="inbox" />;
  else if (path === '/account/notifications/settings') page = <NotificationsPage tab="settings" />;
  else if (path === '/account/support') page = <SupportPage />;
  else page = <p className="text-muted">Page not found</p>;

  return (
    <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto bg-bg text-[14px]">
      <div className="mx-auto flex w-full max-w-[1720px] flex-col gap-8 px-4 py-8 md:px-10 xl:px-[100px] xl:py-12">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
          <div className="flex min-w-0 items-center gap-3.5">
            <div className="grid size-12 shrink-0 place-items-center rounded-full bg-accent-bg text-base font-semibold text-accent-fg">{initials}</div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="truncate text-[17px] font-semibold">{name}</span>
                <KycBadge status={kyc} className="!px-2 !py-0.5 !text-[12px]" />
              </div>
              {live && (
                <CopyButton value={live.id} kind="secondary" size="sm" className="num !h-auto !bg-transparent !p-0 !text-[13px] !font-normal text-muted hover:text-fg">
                  <span title="Copy account ID">{shortAccountId(live.id)}</span> <AIcon name="copy" size={13} />
                </CopyButton>
              )}
            </div>
          </div>
          <div className="flex-1" />
          <SectionPicker path={path} locked={kyc !== 'approved'} />
          <nav aria-label="Account" className="flex overflow-x-auto max-md:hidden">
            <div className="flex gap-1 rounded-full bg-track p-1">
              {NAV.map((n) => {
                const on = n.match(path);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    aria-current={on ? 'page' : undefined}
                    className={cx(
                      'flex shrink-0 items-center gap-1.5 rounded-full px-5 py-2.5 text-[15px] font-semibold whitespace-nowrap no-underline',
                      on ? 'bg-card text-fg shadow-sm' : 'text-muted hover:text-fg',
                    )}
                  >
                    {n.label}
                    {n.to === '/account/withdraw' && kyc !== 'approved' && <AIcon name="lock" size={13} />}
                    {n.to === '/account/documents' && <PendingBadge n={pending} />}
                  </Link>
                );
              })}
            </div>
          </nav>
        </div>

        {showBanner && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-alert px-5 py-4">
            <WarnIcon size={22} className="shrink-0 text-alert-fg" />
            <div className="min-w-[12rem] flex-1 text-[15px]">
              <b>Verify your identity to enable withdrawals.</b>{' '}
              <span className="text-muted">
                {kyc === 'pending'
                  ? 'We are reviewing your documents and will be in touch shortly.'
                  : kyc === 'rejected'
                    ? 'One of your documents was rejected. Upload a new one.'
                    : kyc === 'expired'
                      ? 'Your ID document has expired. Upload a new one.'
                      : 'It takes about 3 minutes.'}
              </span>
            </div>
            <Link to="/account/kyc" className="flex h-11 items-center rounded-full bg-fg px-6 text-[15px] font-semibold text-bg no-underline">
              {kyc === 'pending' ? 'View status' : kyc === 'rejected' || kyc === 'expired' ? 'Resubmit' : 'Verify identity'}
            </Link>
          </div>
        )}

        {(sofBanner || poaBanner) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl bg-alert px-5 py-4">
            <WarnIcon size={22} className="shrink-0 text-alert-fg" />
            <div className="min-w-[12rem] flex-1 text-[15px]">
              <b>{sofBanner ? 'We need your source of funds.' : 'Please update your proof of address.'}</b>{' '}
              <span className="text-muted">
                {sofBanner
                  ? `Your deposits passed the verification limit. Withdrawals are paused until we approve 3 months of bank statements${due ? `; please send them by ${due}` : ''}.`
                  : compliance?.poaStatus === 'rejected'
                    ? 'The last one could not be accepted. Withdrawals are paused until a new one is approved.'
                    : 'The one we have is more than 3 months old. Withdrawals are paused until a new one is approved.'}
              </span>
            </div>
            <Link to={sofBanner ? '/account/source-of-funds' : '/account/kyc'} className="flex h-11 items-center rounded-full bg-fg px-6 text-[15px] font-semibold text-bg no-underline">
              {sofBanner ? 'Send statements' : 'Upload new one'}
            </Link>
          </div>
        )}

        {page}
      </div>
    </div>
  );
}
